import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/helpers";
import { ArrowLeft, Car, Edit, StickyNote } from "lucide-react";
import { toast } from "sonner";

export default function InstructorDetail() {
  const { instructorId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [noteText, setNoteText] = useState("");
  const isStaff = user?.role === "admin" || user?.role === "office_staff";

  const load = useCallback(async () => {
    try {
      const [instRes, vehRes, studRes] = await Promise.all([
        api.get(`/instructors/${instructorId}`),
        api.get("/vehicles"),
        api.get("/students", { params: { instructor_id: instructorId } }),
      ]);
      setInst(instRes.data);
      setEditForm(instRes.data);
      setVehicles(vehRes.data);
      setStudents(studRes.data);
    } catch {
      toast.error("Could not load instructor");
    } finally {
      setLoading(false);
    }
  }, [instructorId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    try {
      await api.put(`/instructors/${instructorId}`, {
        full_name: editForm.full_name,
        phone: editForm.phone,
        address: editForm.address,
        hourly_rate: editForm.hourly_rate,
        status: editForm.status,
        assigned_vehicle_id: editForm.assigned_vehicle_id,
      });
      toast.success("Instructor updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update instructor");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/instructors/${instructorId}/notes`, { note: noteText });
      setNoteText("");
      load();
    } catch {
      toast.error("Could not add note");
    }
  };

  if (loading || !inst) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="instructor-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="instructor-detail-back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{inst.full_name}</h1>
          <p className="text-sm text-muted-foreground">{inst.email} {inst.phone && `· ${inst.phone}`}</p>
        </div>
        {isStaff && (
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-instructor-button">
            <Edit className="h-4 w-4 mr-2" /> Edit
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
            <h2 className="font-semibold font-heading">Overview</h2>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Status: </span><StatusBadge status={inst.status} /></div>
              <div><span className="text-muted-foreground">Hourly rate: </span>{inst.hourly_rate ? `$${inst.hourly_rate}/hr` : "-"}</div>
              <div className="sm:col-span-2 flex items-center gap-1">
                <Car className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Assigned vehicle: </span>
                {inst.assigned_vehicle ? `${inst.assigned_vehicle.make} ${inst.assigned_vehicle.model} (${inst.assigned_vehicle.license_plate})` : "None"}
              </div>
            </div>
          </section>

          <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
            <h2 className="font-semibold font-heading">Assigned Students ({students.length})</h2>
            {students.length === 0 ? (
              <p className="text-sm text-muted-foreground">No students assigned yet.</p>
            ) : (
              <div className="divide-y">
                {students.map((s) => (
                  <button key={s.id} onClick={() => navigate(`/students/${s.id}`)} className="w-full text-left py-2 hover:text-[hsl(var(--role-accent))]" data-testid="instructor-student-row">
                    {s.full_name}
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>

        <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
          <h2 className="font-semibold font-heading flex items-center gap-2"><StickyNote className="h-4 w-4" /> Admin Notes</h2>
          {isStaff && (
            <div className="space-y-2">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note..." data-testid="instructor-note-input" />
              <Button size="sm" onClick={addNote} data-testid="instructor-note-submit-button">Add Note</Button>
            </div>
          )}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(inst.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="text-sm border-l-2 border-[hsl(var(--role-accent))] pl-3">
                <p>{n.note}</p>
                <p className="text-xs text-muted-foreground">{n.created_by} · {formatDateTime(n.created_at)}</p>
              </div>
            ))}
            {(!inst.notes || inst.notes.length === 0) && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
        </section>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-instructor-dialog">
          <DialogHeader>
            <DialogTitle>Edit Instructor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={editForm.full_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={editForm.phone || ""} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Hourly rate ($)</Label>
              <Input type="number" value={editForm.hourly_rate || ""} onChange={(e) => setEditForm((f) => ({ ...f, hourly_rate: parseFloat(e.target.value) }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={editForm.status || "active"} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Assigned vehicle</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={editForm.assigned_vehicle_id || ""} onChange={(e) => setEditForm((f) => ({ ...f, assigned_vehicle_id: e.target.value }))} data-testid="edit-instructor-vehicle-select">
                <option value="">None</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{v.make} {v.model} - {v.license_plate}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-instructor-save-button">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
